import { KeyPair, encryptKeysForStorage, decryptKeysFromStorage, generateUID } from './crypto';
import nacl from 'tweetnacl';
import { encodeBase64, decodeBase64 } from 'tweetnacl-util';
import { safeSetItem } from './safeStorage';
import { idbGet, idbSet } from './indexedDbStorage';
import { isSystemSignal } from './systemSignals';

const KEYS_STORAGE_KEY = 'vaultmsg_encrypted_keys';
const UID_STORAGE_KEY = 'vaultmsg_uid';
const UNLOCK_CODE_STORAGE_KEY = 'vaultmsg_encrypted_unlock_code';
const DEVICE_ID_KEY = 'aphenis_device_id';
const LOCAL_VERIFICATION_KEY = 'aphenis_local_verification';

// Grace period for local verification (48 hours in milliseconds)
const VERIFICATION_GRACE_PERIOD_MS = 48 * 60 * 60 * 1000;

export interface LocalVerification {
  deviceId: string;
  unlockCodeHash: string;
  lastVerified: number;
  activatedAt: number;
  isValid: boolean;
}

export function getOrCreateDeviceId(): string {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    const randomBytes = nacl.randomBytes(24);
    const stripped = encodeBase64(randomBytes).replace(/[^a-zA-Z0-9]/g, '');
    deviceId = stripped.substring(0, 24).padEnd(24, '0');
    safeSetItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
}

function hashUnlockCode(code: string): string {
  const codeBytes = new TextEncoder().encode(code.toUpperCase());
  const bytes = new Uint8Array(codeBytes);
  const hash = nacl.hash(bytes);
  return encodeBase64(hash).substring(0, 32);
}

export function storeLocalVerification(unlockCode: string): void {
  const deviceId = getOrCreateDeviceId();
  const now = Date.now();
  const existing = getLocalVerification();
  const verification: LocalVerification = {
    deviceId,
    unlockCodeHash: hashUnlockCode(unlockCode),
    lastVerified: now,
    activatedAt: existing?.activatedAt || now,
    isValid: true
  };
  safeSetItem(LOCAL_VERIFICATION_KEY, JSON.stringify(verification));
}

export function getLocalVerification(): LocalVerification | null {
  const data = localStorage.getItem(LOCAL_VERIFICATION_KEY);
  if (!data) return null;
  try {
    const verification = JSON.parse(data) as LocalVerification;
    const currentDeviceId = getOrCreateDeviceId();
    if (verification.deviceId !== currentDeviceId) return null;
    return verification;
  } catch { return null; }
}

export function isLocalVerificationValid(): boolean {
  const verification = getLocalVerification();
  if (!verification) return false;
  if (!verification.isValid) return false;
  return Date.now() - verification.lastVerified < VERIFICATION_GRACE_PERIOD_MS;
}

export function updateLastVerified(): void {
  const verification = getLocalVerification();
  if (verification) {
    verification.lastVerified = Date.now();
    safeSetItem(LOCAL_VERIFICATION_KEY, JSON.stringify(verification));
  }
}

export function invalidateLocalVerification(): void {
  const verification = getLocalVerification();
  if (verification) {
    verification.isValid = false;
    safeSetItem(LOCAL_VERIFICATION_KEY, JSON.stringify(verification));
  }
  localStorage.removeItem('aphenis_unlocked');
  localStorage.removeItem('aphenis_activation_info');
}

export function validateUnlockCodeLocally(code: string): boolean {
  const verification = getLocalVerification();
  if (!verification) return false;
  return hashUnlockCode(code) === verification.unlockCodeHash && verification.isValid;
}

export function shouldSkipServerVerification(): boolean {
  return isLocalVerificationValid();
}

export function clearLocalVerification(): void {
  localStorage.removeItem(LOCAL_VERIFICATION_KEY);
}

export interface StoredIdentity {
  uid: string;
  signingPublicKey: string;
  encryptionPublicKey: string;
}

async function encryptUnlockCode(code: string, passphrase: string): Promise<string> {
  const salt = nacl.randomBytes(32);
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw', encoder.encode(passphrase), 'PBKDF2', false, ['deriveBits']
  );
  const derivedBits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt: salt.buffer as ArrayBuffer, iterations: 100000, hash: 'SHA-256' },
    keyMaterial, nacl.secretbox.keyLength * 8
  );
  const key = new Uint8Array(derivedBits);
  const nonce = nacl.randomBytes(nacl.secretbox.nonceLength);
  const codeBytes = new TextEncoder().encode(code);
  const encrypted = nacl.secretbox(codeBytes, nonce, key);
  return JSON.stringify({
    encrypted: encodeBase64(encrypted), nonce: encodeBase64(nonce),
    salt: encodeBase64(salt), version: 3
  });
}

function decryptUnlockCodeLegacy(encryptedData: string, passphrase: string): string | null {
  try {
    const { encrypted, nonce, salt } = JSON.parse(encryptedData);
    const passphraseBytes = new TextEncoder().encode(passphrase);
    let hash = nacl.hash(new Uint8Array([...passphraseBytes, ...decodeBase64(salt)]));
    for (let i = 0; i < 1000; i++) hash = nacl.hash(hash);
    const key = hash.slice(0, nacl.secretbox.keyLength);
    const decrypted = nacl.secretbox.open(decodeBase64(encrypted), decodeBase64(nonce), key);
    if (!decrypted) return null;
    return new TextDecoder().decode(decrypted);
  } catch { return null; }
}

async function decryptUnlockCode(encryptedData: string, passphrase: string): Promise<string | null> {
  try {
    const parsed = JSON.parse(encryptedData);
    const { encrypted, nonce, salt, version } = parsed;
    if (version === 3) {
      const encoder = new TextEncoder();
      const keyMaterial = await crypto.subtle.importKey(
        'raw', encoder.encode(passphrase), 'PBKDF2', false, ['deriveBits']
      );
      const derivedBits = await crypto.subtle.deriveBits(
        { name: 'PBKDF2', salt: decodeBase64(salt).buffer as ArrayBuffer, iterations: 100000, hash: 'SHA-256' },
        keyMaterial, nacl.secretbox.keyLength * 8
      );
      const key = new Uint8Array(derivedBits);
      const decrypted = nacl.secretbox.open(decodeBase64(encrypted), decodeBase64(nonce), key);
      if (!decrypted) return null;
      return new TextDecoder().decode(decrypted);
    } else {
      return decryptUnlockCodeLegacy(encryptedData, passphrase);
    }
  } catch { return null; }
}

export async function storeEncryptedKeys(
  keys: { signing: KeyPair; encryption: KeyPair },
  passphrase: string,
  unlockCode?: string
): Promise<void> {
  const encrypted = await encryptKeysForStorage(keys, passphrase);
  safeSetItem(KEYS_STORAGE_KEY, encrypted);
  const uid = generateUID(keys.signing.publicKey);
  const identity: StoredIdentity = {
    uid, signingPublicKey: keys.signing.publicKey, encryptionPublicKey: keys.encryption.publicKey,
  };
  safeSetItem(UID_STORAGE_KEY, JSON.stringify(identity));
  if (unlockCode) {
    const encryptedCode = await encryptUnlockCode(unlockCode, passphrase);
    safeSetItem(UNLOCK_CODE_STORAGE_KEY, encryptedCode);
  }
}

export function getStoredIdentity(): StoredIdentity | null {
  const data = localStorage.getItem(UID_STORAGE_KEY);
  if (!data) return null;
  try { return JSON.parse(data); } catch { return null; }
}

export async function retrieveDecryptedKeys(
  passphrase: string
): Promise<{ signing: KeyPair; encryption: KeyPair; unlockCode?: string } | null> {
  const encrypted = localStorage.getItem(KEYS_STORAGE_KEY);
  if (!encrypted) return null;
  const keys = await decryptKeysFromStorage(encrypted, passphrase);
  if (!keys) return null;
  const encryptedCode = localStorage.getItem(UNLOCK_CODE_STORAGE_KEY);
  let unlockCode: string | undefined;
  if (encryptedCode) {
    const decrypted = await decryptUnlockCode(encryptedCode, passphrase);
    if (decrypted) unlockCode = decrypted;
  }
  return { ...keys, unlockCode };
}

export async function storeUnlockCode(code: string, passphrase: string): Promise<void> {
  const encryptedCode = await encryptUnlockCode(code, passphrase);
  safeSetItem(UNLOCK_CODE_STORAGE_KEY, encryptedCode);
}

export function getEncryptedUnlockCode(): string | null {
  return localStorage.getItem(UNLOCK_CODE_STORAGE_KEY);
}

export function hasStoredKeys(): boolean {
  return localStorage.getItem(KEYS_STORAGE_KEY) !== null;
}

export function hasStoredUnlockCode(): boolean {
  return localStorage.getItem(UNLOCK_CODE_STORAGE_KEY) !== null;
}

export function clearStoredKeys(): void {
  localStorage.removeItem(KEYS_STORAGE_KEY);
  localStorage.removeItem(UID_STORAGE_KEY);
  localStorage.removeItem(UNLOCK_CODE_STORAGE_KEY);
}

// Message storage (temporary, encrypted locally)
export interface MessageEdit {
  content: string;
  editedAt: number;
  signature: string;
}

export interface LocalMessage {
  id: string;
  chatId: string;
  senderId: string;
  content: string;
  timestamp: number;
  encrypted: boolean;
  burnAfterView?: boolean;
  viewed?: boolean;
  viewedBy?: string[];
  verified?: boolean;
  mediaType?: 'image' | 'video' | 'audio' | 'file';
  mediaUrl?: string;
  fileName?: string;
  deliveryStatus?: 'sent' | 'delivered' | 'failed';
  reactions?: Record<string, string[]>;
  replyTo?: { id: string; content: string; senderId: string };
  scheduledAt?: number;
  expiresIn?: number;
  editHistory?: MessageEdit[];
  edited?: boolean;
  disappearingMedia?: boolean;
  mediaViewed?: boolean;
  systemType?: 'voice-call' | 'video-call';
  callDuration?: number;
  deleted?: boolean;
  storageMedia?: {
    storagePath: string;
    fileKey: string;
    fileNonce: string;
    mimeType: string;
    originalSize: number;
  };
  isPublicModeMsg?: boolean;
  listenOnce?: boolean;
}

// ─── IndexedDB-backed Message Storage ───
// Messages are now stored in IndexedDB to avoid localStorage quota issues.
// The IDB key is the same as the old localStorage key for migration compat.
const MESSAGES_IDB_KEY = 'vaultmsg_messages';
const MESSAGES_KEY = 'vaultmsg_messages'; // legacy localStorage key

// In-memory cache
let _msgCache: LocalMessage[] | null = null;
let _msgCacheVersion = 0;
let _idbLoadPromise: Promise<void> | null = null;
let _idbLoaded = false;

function _invalidateMsgCache(): void {
  _msgCache = null;
  _msgCacheVersion++;
}

/** 
 * Persist messages to IndexedDB (primary) with localStorage fallback.
 * The in-memory cache is always kept in sync.
 */
function _persistMessages(messages: LocalMessage[]): void {
  _msgCache = messages;
  _msgCacheVersion++;
  // Async write to IndexedDB — fire and forget
  idbSet(MESSAGES_IDB_KEY, JSON.stringify(messages)).catch(e => {
    console.warn('[Storage] IDB persist failed, trying localStorage fallback:', e);
    try {
      safeSetItem(MESSAGES_KEY, JSON.stringify(messages));
    } catch (lsErr) {
      console.warn('[Storage] localStorage fallback also failed, trimming:', lsErr);
      const trimmed = messages.slice(-500);
      safeSetItem(MESSAGES_KEY, JSON.stringify(trimmed));
    }
  });
}

/**
 * Load messages from IndexedDB on first access, falling back to localStorage.
 * Subsequent calls return from cache instantly.
 */
async function _ensureMessagesLoaded(): Promise<void> {
  if (_idbLoaded) return;
  if (_idbLoadPromise) return _idbLoadPromise;
  _idbLoadPromise = (async () => {
    try {
      const idbData = await idbGet(MESSAGES_IDB_KEY);
      if (idbData) {
        _msgCache = JSON.parse(idbData);
        // Remove from localStorage if it still exists there (one-time migration)
        try { localStorage.removeItem(MESSAGES_KEY); } catch {}
      } else {
        // Migrate from localStorage to IndexedDB
        const lsData = localStorage.getItem(MESSAGES_KEY);
        if (lsData) {
          _msgCache = JSON.parse(lsData);
          // Write to IDB and remove from localStorage
          await idbSet(MESSAGES_IDB_KEY, lsData).catch(() => {});
          try { localStorage.removeItem(MESSAGES_KEY); } catch {}
          console.log('[Storage] Migrated messages from localStorage to IndexedDB');
        } else {
          _msgCache = [];
        }
      }
    } catch {
      // Fallback: try localStorage
      try {
        const lsData = localStorage.getItem(MESSAGES_KEY);
        _msgCache = lsData ? JSON.parse(lsData) : [];
      } catch {
        _msgCache = [];
      }
    }
    _idbLoaded = true;
  })();
  return _idbLoadPromise;
}

// Kick off loading immediately on module import
_ensureMessagesLoaded();

// Soft-delete a message
export function softDeleteMessage(messageId: string): void {
  const messages = getMessages();
  const idx = messages.findIndex(m => m.id === messageId);
  if (idx === -1) return;
  messages[idx].content = '';
  messages[idx].mediaUrl = undefined;
  messages[idx].mediaType = undefined;
  messages[idx].fileName = undefined;
  messages[idx].storageMedia = undefined;
  messages[idx].deleted = true;
  messages[idx].reactions = undefined;
  messages[idx].editHistory = undefined;
  messages[idx].edited = undefined;
  _persistMessages(messages);
}

export function editMessage(messageId: string, newContent: string, signature: string): void {
  const messages = getMessages();
  const idx = messages.findIndex(m => m.id === messageId);
  if (idx === -1) return;
  const msg = messages[idx];
  if (!msg.editHistory) msg.editHistory = [];
  msg.editHistory.push({ content: msg.content, editedAt: Date.now(), signature });
  msg.content = newContent;
  msg.edited = true;
  _persistMessages(messages);
}

export function markMediaViewed(messageId: string): void {
  const messages = getMessages();
  const idx = messages.findIndex(m => m.id === messageId);
  if (idx === -1) return;
  if (messages[idx].disappearingMedia) {
    messages[idx].mediaViewed = true;
    setTimeout(() => {
      const msgs = getMessages();
      const i = msgs.findIndex(m => m.id === messageId);
      if (i !== -1 && msgs[i].mediaViewed && msgs[i].disappearingMedia && msgs[i].mediaUrl) {
        msgs[i].mediaUrl = undefined;
        msgs[i].mediaType = undefined;
        msgs[i].content = msgs[i].content || '[Media expired]';
        _persistMessages(msgs);
      }
    }, 5000);
  }
  _persistMessages(messages);
}

export function updateMessageId(oldId: string, newId: string): void {
  const messages = getMessages();
  const idx = messages.findIndex(m => m.id === oldId);
  if (idx === -1) return;
  messages[idx].id = newId;
  _persistMessages(messages);
}

export function addReaction(messageId: string, emoji: string, senderUid: string, toggle: boolean = true): void {
  const messages = getMessages();
  const idx = messages.findIndex(m => m.id === messageId);
  if (idx === -1) return;
  const msg = messages[idx];
  if (!msg.reactions) msg.reactions = {};
  if (!msg.reactions[emoji]) msg.reactions[emoji] = [];
  if (!msg.reactions[emoji].includes(senderUid)) {
    msg.reactions[emoji].push(senderUid);
  } else if (toggle) {
    msg.reactions[emoji] = msg.reactions[emoji].filter(u => u !== senderUid);
    if (msg.reactions[emoji].length === 0) delete msg.reactions[emoji];
  }
  _persistMessages(messages);
}

export function removeReaction(messageId: string, emoji: string, senderUid: string): void {
  const messages = getMessages();
  const idx = messages.findIndex(m => m.id === messageId);
  if (idx === -1) return;
  const msg = messages[idx];
  if (!msg.reactions?.[emoji]) return;
  msg.reactions[emoji] = msg.reactions[emoji].filter(u => u !== senderUid);
  if (msg.reactions[emoji].length === 0) delete msg.reactions[emoji];
  _persistMessages(messages);
}

// Scheduled messages
const SCHEDULED_KEY = 'aphenis_scheduled_messages';

export interface ScheduledMessage {
  id: string;
  chatId: string;
  content: string;
  scheduledAt: number;
  options?: { burnAfterView?: boolean; silent?: boolean };
}

export function getScheduledMessages(): ScheduledMessage[] {
  try {
    const data = localStorage.getItem(SCHEDULED_KEY);
    return data ? JSON.parse(data) : [];
  } catch { return []; }
}

export function addScheduledMessage(msg: ScheduledMessage): void {
  const all = getScheduledMessages();
  all.push(msg);
  safeSetItem(SCHEDULED_KEY, JSON.stringify(all));
}

export function removeScheduledMessage(id: string): void {
  const all = getScheduledMessages().filter(m => m.id !== id);
  safeSetItem(SCHEDULED_KEY, JSON.stringify(all));
}

export function getDueScheduledMessages(): ScheduledMessage[] {
  return getScheduledMessages().filter(m => Date.now() >= m.scheduledAt);
}

// Message search
export function searchMessages(query: string, chatId?: string): LocalMessage[] {
  const q = query.toLowerCase();
  let msgs = getMessages();
  if (chatId) msgs = msgs.filter(m => m.chatId === chatId);
  return msgs.filter(m => m.content.toLowerCase().includes(q));
}

// Per-chat read receipts
const READ_RECEIPTS_KEY = 'aphenis_read_receipts_off';

export function getReadReceiptsDisabledChats(): string[] {
  try {
    const data = localStorage.getItem(READ_RECEIPTS_KEY);
    return data ? JSON.parse(data) : [];
  } catch { return []; }
}

export function toggleReadReceiptsForChat(chatId: string): boolean {
  const disabled = getReadReceiptsDisabledChats();
  const idx = disabled.indexOf(chatId);
  if (idx !== -1) {
    disabled.splice(idx, 1);
    safeSetItem(READ_RECEIPTS_KEY, JSON.stringify(disabled));
    return true;
  } else {
    disabled.push(chatId);
    safeSetItem(READ_RECEIPTS_KEY, JSON.stringify(disabled));
    return false;
  }
}

export function storeMessage(message: LocalMessage): void {
  const messages = getMessages();
  if (messages.some(m => m.id === message.id)) return;
  // Block system signals from being persisted — they are processed in real-time only
  if (isSystemSignal(message.content)) return;
  messages.push(message);
  _persistMessages(messages);
}

export function updateMessageMedia(
  messageId: string,
  fields: Partial<Pick<LocalMessage, 'mediaUrl' | 'storageMedia' | 'mediaType' | 'fileName'>>
): void {
  const messages = getMessages();
  const idx = messages.findIndex(m => m.id === messageId);
  if (idx === -1) return;
  const msg = messages[idx];
  if (fields.mediaUrl !== undefined) msg.mediaUrl = fields.mediaUrl;
  if (fields.storageMedia !== undefined) msg.storageMedia = fields.storageMedia;
  if (fields.mediaType !== undefined) msg.mediaType = fields.mediaType;
  if (fields.fileName !== undefined) msg.fileName = fields.fileName;
  _persistMessages(messages);
}

/**
 * Get all messages. Returns from in-memory cache (synchronous).
 * If IDB hasn't finished loading yet, returns what's available.
 */
export function getMessages(): LocalMessage[] {
  if (_msgCache !== null) return [..._msgCache];
  // IDB might not have loaded yet — try localStorage as sync fallback
  try {
    const data = localStorage.getItem(MESSAGES_KEY);
    if (data) {
      const parsed = JSON.parse(data) as LocalMessage[];
      _msgCache = parsed;
      return [...parsed];
    }
  } catch {}
  return [];
}

export function getChatMessages(chatId: string): LocalMessage[] {
  return getMessages().filter((m) => m.chatId === chatId);
}

export function getLastMessagePerChat(): Map<string, LocalMessage> {
  const map = new Map<string, LocalMessage>();
  const msgs = getMessages();
  for (const m of msgs) {
    if (isSystemSignal(m.content)) continue;
    if (m.deleted) continue;
    const existing = map.get(m.chatId);
    if (!existing || m.timestamp > existing.timestamp) {
      map.set(m.chatId, m);
    }
  }
  return map;
}

export function deleteMessage(messageId: string): void {
  const messages = getMessages().filter((m) => m.id !== messageId);
  _persistMessages(messages);
}

export function markMessageAsViewed(messageId: string, viewerUid?: string): void {
  const messages = getMessages();
  const index = messages.findIndex((m) => m.id === messageId);
  if (index !== -1) {
    const msg = messages[index];
    if (viewerUid) {
      if (!msg.viewedBy) msg.viewedBy = [];
      if (!msg.viewedBy.includes(viewerUid)) msg.viewedBy.push(viewerUid);
    }
    messages[index].viewed = true;
    if (messages[index].burnAfterView) {
      if (!viewerUid) setTimeout(() => deleteMessage(messageId), 3000);
    }
    _persistMessages(messages);
  }
}

export function checkAllGroupMembersViewed(messageId: string, totalMembers: number): boolean {
  const messages = getMessages();
  const msg = messages.find(m => m.id === messageId);
  if (!msg?.viewedBy) return false;
  return msg.viewedBy.length >= (totalMembers - 1);
}

export function scheduleGroupBurnDeletion(messageId: string): void {
  setTimeout(() => deleteMessage(messageId), 3000);
}

// Contact storage
export interface Contact {
  uid: string;
  publicKey: string;
  encryptionPublicKey: string;
  alias?: string;
  aliasNameEffect?: string;
  addedAt: number;
  pendingRequest?: boolean;
  verified?: boolean;
  blocked?: boolean;
  deleted?: boolean;
}

const CONTACTS_KEY = 'vaultmsg_contacts';

export function storeContact(contact: Contact): void {
  const contacts = getContacts();
  const existingIndex = contacts.findIndex((c) => c.uid === contact.uid);
  if (existingIndex !== -1) contacts[existingIndex] = contact;
  else contacts.push(contact);
  safeSetItem(CONTACTS_KEY, JSON.stringify(contacts));
}

export function getContacts(): Contact[] {
  const data = localStorage.getItem(CONTACTS_KEY);
  if (!data) return [];
  try { return JSON.parse(data); } catch { return []; }
}

export function getContact(uid: string): Contact | undefined {
  return getContacts().find((c) => c.uid === uid);
}

export function deleteContact(uid: string): void {
  const contacts = getContacts().filter((c) => c.uid !== uid);
  safeSetItem(CONTACTS_KEY, JSON.stringify(contacts));
}

export function softDeleteContact(uid: string): void {
  const contacts = getContacts();
  const idx = contacts.findIndex(c => c.uid === uid);
  if (idx === -1) return;
  contacts[idx].deleted = true;
  safeSetItem(CONTACTS_KEY, JSON.stringify(contacts));
}

export function permanentDeleteContact(uid: string): void {
  deleteAllChatData(uid);
  deleteContact(uid);
}

export function restoreContact(uid: string): void {
  const contacts = getContacts();
  const idx = contacts.findIndex(c => c.uid === uid);
  if (idx === -1) return;
  contacts[idx].deleted = false;
  safeSetItem(CONTACTS_KEY, JSON.stringify(contacts));
}

export function blockContact(uid: string): void {
  const contacts = getContacts();
  const idx = contacts.findIndex(c => c.uid === uid);
  if (idx === -1) return;
  contacts[idx].blocked = true;
  safeSetItem(CONTACTS_KEY, JSON.stringify(contacts));
}

export function unblockContact(uid: string): void {
  const contacts = getContacts();
  const idx = contacts.findIndex(c => c.uid === uid);
  if (idx === -1) return;
  contacts[idx].blocked = false;
  safeSetItem(CONTACTS_KEY, JSON.stringify(contacts));
}

export function isContactBlocked(uid: string): boolean {
  return getContact(uid)?.blocked === true;
}

export function isContactDeleted(uid: string): boolean {
  return getContact(uid)?.deleted === true;
}

// Group chat storage
export interface GroupAnnouncement {
  text: string;
  setBy: string;
  setAt: number;
}

export interface GroupChat {
  id: string;
  name: string;
  topic?: string;
  members: string[];
  createdBy: string;
  createdAt: number;
  groupPhoto?: string;
  announcement?: GroupAnnouncement;
}

const GROUPS_KEY = 'vaultmsg_groups';

export function storeGroup(group: GroupChat): void {
  const groups = getGroups();
  const existingIndex = groups.findIndex((g) => g.id === group.id);
  if (existingIndex !== -1) groups[existingIndex] = group;
  else groups.push(group);
  safeSetItem(GROUPS_KEY, JSON.stringify(groups));
}

export function getGroups(): GroupChat[] {
  const data = localStorage.getItem(GROUPS_KEY);
  if (!data) return [];
  try { return JSON.parse(data); } catch { return []; }
}

// Self-destructing conversation timers
const CHAT_TIMERS_KEY = 'aphenis_chat_timers';

export interface ChatTimer {
  chatId: string;
  duration: number;
  startedAt: number;
}

export function getChatTimers(): ChatTimer[] {
  try {
    const data = localStorage.getItem(CHAT_TIMERS_KEY);
    return data ? JSON.parse(data) : [];
  } catch { return []; }
}

export function setChatTimer(chatId: string, duration: number): void {
  const timers = getChatTimers().filter(t => t.chatId !== chatId);
  if (duration > 0) timers.push({ chatId, duration, startedAt: Date.now() });
  safeSetItem(CHAT_TIMERS_KEY, JSON.stringify(timers));
}

export function getChatTimer(chatId: string): ChatTimer | undefined {
  return getChatTimers().find(t => t.chatId === chatId);
}

export function clearChatTimer(chatId: string): void {
  const timers = getChatTimers().filter(t => t.chatId !== chatId);
  safeSetItem(CHAT_TIMERS_KEY, JSON.stringify(timers));
}

export function cleanupExpiredChats(): void {
  const timers = getChatTimers();
  const now = Date.now();
  const expired = timers.filter(t => now >= t.startedAt + t.duration);
  if (expired.length === 0) return;
  const messages = getMessages();
  const expiredIds = new Set(expired.map(t => t.chatId));
  const remaining = messages.filter(m => !expiredIds.has(m.chatId));
  _persistMessages(remaining);
  const activeTimers = timers.filter(t => now < t.startedAt + t.duration);
  safeSetItem(CHAT_TIMERS_KEY, JSON.stringify(activeTimers));
}

export function getGroup(id: string): GroupChat | undefined {
  return getGroups().find((g) => g.id === id);
}

export function deleteGroup(id: string): void {
  const groups = getGroups().filter((g) => g.id !== id);
  safeSetItem(GROUPS_KEY, JSON.stringify(groups));
}

// ─── Per-Contact Security Levels ───
export type SecurityLevel = 'standard' | 'elevated' | 'maximum';

export interface ContactSecurityConfig {
  contactId: string;
  level: SecurityLevel;
  autoBurn: boolean;
  disappearingMedia: boolean;
  disableReadReceipts: boolean;
  autoSelfDestruct: number;
}

const SECURITY_LEVELS_KEY = 'aphenis_contact_security_levels';

export const SECURITY_LEVEL_PRESETS: Record<SecurityLevel, Omit<ContactSecurityConfig, 'contactId' | 'level'>> = {
  standard: { autoBurn: false, disappearingMedia: false, disableReadReceipts: false, autoSelfDestruct: 0 },
  elevated: { autoBurn: true, disappearingMedia: false, disableReadReceipts: true, autoSelfDestruct: 0 },
  maximum: { autoBurn: true, disappearingMedia: true, disableReadReceipts: true, autoSelfDestruct: 24 * 60 * 60 * 1000 },
};

export function getContactSecurityConfig(contactId: string): ContactSecurityConfig {
  try {
    const data = localStorage.getItem(SECURITY_LEVELS_KEY);
    const configs: ContactSecurityConfig[] = data ? JSON.parse(data) : [];
    return configs.find(c => c.contactId === contactId) || { contactId, level: 'standard', ...SECURITY_LEVEL_PRESETS.standard };
  } catch { return { contactId, level: 'standard', ...SECURITY_LEVEL_PRESETS.standard }; }
}

export function setContactSecurityLevel(contactId: string, level: SecurityLevel): ContactSecurityConfig {
  const config: ContactSecurityConfig = { contactId, level, ...SECURITY_LEVEL_PRESETS[level] };
  saveContactSecurityConfig(config);
  return config;
}

export function saveContactSecurityConfig(config: ContactSecurityConfig): void {
  try {
    const data = localStorage.getItem(SECURITY_LEVELS_KEY);
    const configs: ContactSecurityConfig[] = data ? JSON.parse(data) : [];
    const idx = configs.findIndex(c => c.contactId === config.contactId);
    if (idx >= 0) configs[idx] = config; else configs.push(config);
    safeSetItem(SECURITY_LEVELS_KEY, JSON.stringify(configs));
  } catch {}
}

// ─── Encrypted Data Backup (contacts + messages) ───

export async function encryptDataBackup(passphrase: string): Promise<string> {
  const contacts = getContacts();
  const messages = getMessages();
  const groups = getGroups();
  const chatPrefs = localStorage.getItem('aphenis_pinned_chats');
  const vaultMessages = getVaultMessages();

  const { idbGet: _idbGet } = await import('@/lib/indexedDbStorage');
  const profilePicture = await _idbGet('aphenis_profile_picture');
  const userAlias = localStorage.getItem('aphenis_user_alias');
  const nameEffect = localStorage.getItem('aphenis_name_effect');
  const nameEffectConfig = localStorage.getItem('aphenis_name_effect_config');
  const contactProfiles = localStorage.getItem('aphenis_contact_profiles');
  
  const contactPfps: Record<string, string> = {};
  if (contactProfiles) {
    try {
      const parsed = JSON.parse(contactProfiles);
      for (const uid of Object.keys(parsed)) {
        const pic = await _idbGet(`contact_pfp_${uid}`);
        if (pic) contactPfps[uid] = pic;
      }
    } catch {}
  }

  const chatEffects = localStorage.getItem('aphenis_chat_effects');
  const globalChatEffect = localStorage.getItem('aphenis_global_chat_effect');
  const ambientSettings = localStorage.getItem('aphenis_ambient_settings');
  const bubbleStyle = localStorage.getItem('aphenis_bubble_style');
  const chatBubbleStyles = localStorage.getItem('aphenis_chat_bubble_styles');
  const chatThemes = localStorage.getItem('aphenis_chat_themes');
  const globalChatTheme = localStorage.getItem('aphenis_global_chat_theme');
  const customThemes = localStorage.getItem('aphenis_custom_themes');
  const archivedChats = localStorage.getItem('aphenis_archived_chats');
  const mutedChats = localStorage.getItem('aphenis_muted_chats');
  const privacySettings = localStorage.getItem('vaultmsg_privacy_settings');
  const contactSecurityLevels = localStorage.getItem('aphenis_contact_security_levels');
  const readReceiptsOff = localStorage.getItem('aphenis_read_receipts_off');
  const chatFolders = localStorage.getItem('aphenis_chat_folders');
  const chatFolderAssignments = localStorage.getItem('aphenis_chat_folder_assignments');
  const vaultPin = localStorage.getItem('aphenis_vault_pin');
  const chatThemeExpiry = localStorage.getItem('aphenis_chat_theme_expiry');

  const chatPets = localStorage.getItem('aphenis_chat_pets');
  const petInteractions = localStorage.getItem('aphenis_pet_interactions');
  const primaryPetChatId = localStorage.getItem('aphenis_primary_pet_chat_id');
  const petActivityLog = localStorage.getItem('aphenis_pet_activity_log');
  const petActivityFeed = localStorage.getItem('aphenis_pet_activity_feed');
  const petLastInteractions = localStorage.getItem('aphenis_pet_last_interactions');
  const petInteractionCounts = localStorage.getItem('aphenis_pet_interaction_counts');
  const petLifecycle = localStorage.getItem('aphenis_pet_lifecycle');
  const petRevives = localStorage.getItem('aphenis_pet_revives');
  const petFriendships = localStorage.getItem('aphenis_pet_friendships');
  const petCoopCooldowns = localStorage.getItem('aphenis_pet_coop_cooldowns');
  const petCoopHistory = localStorage.getItem('aphenis_pet_coop_history');
  const petRoaming = localStorage.getItem('aphenis_pet_roaming');
  const petPublic = localStorage.getItem('aphenis_pet_public');
  const petNativeReminders = localStorage.getItem('aphenis_pet_native_reminders');
  const petCareLastReminded = localStorage.getItem('aphenis_pet_care_last_reminded');
  const petWorldHp = localStorage.getItem('aphenis_pet_world_hp');

  const perPetData: Record<string, string> = {};
  const PER_PET_PREFIXES = [
    'aphenis_pet_lifecycle_', 'aphenis_pet_last_interactions_',
    'aphenis_pet_interaction_counts_', 'aphenis_pet_needs_initialized_',
  ];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && PER_PET_PREFIXES.some(p => k.startsWith(p))) {
      const v = localStorage.getItem(k);
      if (v) perPetData[k] = v;
    }
  }

  const petworldGraphics = localStorage.getItem('aphenis_petworld_graphics');
  const petworldVolume = localStorage.getItem('aphenis_petworld_volume');
  const petworldMuted = localStorage.getItem('aphenis_petworld_muted');
  const petworldSfx = localStorage.getItem('aphenis_petworld_sfx');
  const petworldMusic = localStorage.getItem('aphenis_petworld_music');

  const petInventory = localStorage.getItem('aphenis_pet_inventory');
  const equippedSlots = localStorage.getItem('aphenis_equipped_slots');
  const petRecipesDiscovered = localStorage.getItem('aphenis_pet_recipes_discovered');
  const petWorldFogChunks = localStorage.getItem('aphenis_petworld_fog_chunks');
  const petWorldMarkers = localStorage.getItem('aphenis_petworld_markers');
  const petWorldCombatStats = localStorage.getItem('aphenis_pet_world_combatStats');
  const petWorldPurchased: Record<string, string> = {};
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith('aphenis_petworld_purchased_')) {
      const v = localStorage.getItem(k);
      if (v) petWorldPurchased[k] = v;
    }
  }

  const gameHistory = localStorage.getItem('aphenis_game_history');
  const gameStatsPublic = localStorage.getItem('aphenis_game_stats_public');
  const lootPublic = localStorage.getItem('aphenis_loot_public');
  const appXP = localStorage.getItem('aphenis_app_xp');
  const xpLog = localStorage.getItem('aphenis_xp_log');
  const dailyLogin = localStorage.getItem('aphenis_daily_login');
  const levelRewardsClaimed = localStorage.getItem('aphenis_level_rewards_claimed');
  const levelRewardsEquipped = localStorage.getItem('aphenis_level_rewards_equipped');
  const chatStreaks = localStorage.getItem('aphenis_chat_streaks');
  const notificationCenter = localStorage.getItem('aphenis_notification_center');
  const achievements = localStorage.getItem('aphenis_achievements');
  const achievementsSig = localStorage.getItem('aphenis_achievements_sig');
  
  const data = JSON.stringify({
    contacts, messages, groups, chatPrefs, vaultMessages,
    profilePicture, userAlias, nameEffect, nameEffectConfig, contactProfiles,
    contactPfps,
    chatEffects, globalChatEffect, ambientSettings,
    bubbleStyle, chatBubbleStyles,
    chatThemes, globalChatTheme, customThemes, chatThemeExpiry,
    archivedChats, mutedChats,
    chatFolders, chatFolderAssignments,
    privacySettings, contactSecurityLevels, readReceiptsOff, vaultPin,
    chatPets, petInteractions, primaryPetChatId, petActivityLog, petActivityFeed,
    petLastInteractions, petInteractionCounts, petLifecycle, petRevives,
    petFriendships, petCoopCooldowns, petCoopHistory,
    petRoaming, petPublic, petNativeReminders, petCareLastReminded, petWorldHp,
    perPetData,
    petworldGraphics, petworldVolume, petworldMuted, petworldSfx, petworldMusic,
    petInventory, equippedSlots, petRecipesDiscovered, petWorldFogChunks, petWorldMarkers,
    petWorldCombatStats, petWorldPurchased,
    gameHistory, gameStatsPublic,
    lootPublic,
    appXP, xpLog, dailyLogin,
    levelRewardsClaimed, levelRewardsEquipped,
    chatStreaks, notificationCenter,
    achievements, achievementsSig,
    version: 7,
  });
  const dataBytes = new TextEncoder().encode(data);
  
  const salt = nacl.randomBytes(32);
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw', encoder.encode(passphrase), 'PBKDF2', false, ['deriveBits']
  );
  const derivedBits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt: salt.buffer as ArrayBuffer, iterations: 100000, hash: 'SHA-256' },
    keyMaterial, nacl.secretbox.keyLength * 8
  );
  const key = new Uint8Array(derivedBits);
  const nonce = nacl.randomBytes(nacl.secretbox.nonceLength);
  const encrypted = nacl.secretbox(dataBytes, nonce, key);
  
  return JSON.stringify({
    encrypted: encodeBase64(encrypted), nonce: encodeBase64(nonce),
    salt: encodeBase64(salt), version: 3,
  });
}

export async function decryptAndRestoreDataBackup(encryptedBlob: string, passphrase: string): Promise<boolean> {
  try {
    const { encrypted, nonce, salt } = JSON.parse(encryptedBlob);
    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
      'raw', encoder.encode(passphrase), 'PBKDF2', false, ['deriveBits']
    );
    const derivedBits = await crypto.subtle.deriveBits(
      { name: 'PBKDF2', salt: decodeBase64(salt).buffer as ArrayBuffer, iterations: 100000, hash: 'SHA-256' },
      keyMaterial, nacl.secretbox.keyLength * 8
    );
    const key = new Uint8Array(derivedBits);
    const decrypted = nacl.secretbox.open(decodeBase64(encrypted), decodeBase64(nonce), key);
    if (!decrypted) return false;
    
    const data = JSON.parse(new TextDecoder().decode(decrypted));
    
    if (data.contacts && Array.isArray(data.contacts)) {
      const existing = getContacts();
      const existingUids = new Set(existing.map(c => c.uid));
      for (const contact of data.contacts) {
        if (!existingUids.has(contact.uid)) existing.push(contact);
      }
      safeSetItem(CONTACTS_KEY, JSON.stringify(existing));
    }
    
    if (data.messages && Array.isArray(data.messages)) {
      const existing = getMessages();
      const existingIds = new Set(existing.map(m => m.id));
      for (const msg of data.messages) {
        if (!existingIds.has(msg.id)) existing.push(msg);
      }
      _persistMessages(existing);
    }
    
    if (data.groups && Array.isArray(data.groups)) {
      const existing = getGroups();
      const existingIds = new Set(existing.map(g => g.id));
      for (const group of data.groups) {
        if (!existingIds.has(group.id)) existing.push(group);
      }
      safeSetItem(GROUPS_KEY, JSON.stringify(existing));
    }

    if (data.vaultMessages && Array.isArray(data.vaultMessages)) {
      const existing = getVaultMessages();
      const existingIds = new Set(existing.map(v => v.id));
      for (const vm of data.vaultMessages) {
        if (!existingIds.has(vm.id)) existing.push(vm);
      }
      safeSetItem(VAULT_KEY, JSON.stringify(existing));
    }

    const { idbSet: _idbSet } = await import('@/lib/indexedDbStorage');
    if (data.profilePicture) await _idbSet('aphenis_profile_picture', data.profilePicture);
    if (data.userAlias) safeSetItem('aphenis_user_alias', data.userAlias);
    if (data.nameEffect) safeSetItem('aphenis_name_effect', data.nameEffect);
    if (data.nameEffectConfig) safeSetItem('aphenis_name_effect_config', data.nameEffectConfig);
    if (data.contactProfiles) safeSetItem('aphenis_contact_profiles', data.contactProfiles);
    
    if (data.contactPfps && typeof data.contactPfps === 'object') {
      for (const [uid, pic] of Object.entries(data.contactPfps)) {
        if (typeof pic === 'string') await _idbSet(`contact_pfp_${uid}`, pic);
      }
    }

    if (data.chatEffects) safeSetItem('aphenis_chat_effects', data.chatEffects);
    if (data.globalChatEffect) safeSetItem('aphenis_global_chat_effect', data.globalChatEffect);
    if (data.ambientSettings) safeSetItem('aphenis_ambient_settings', data.ambientSettings);
    if (data.bubbleStyle) safeSetItem('aphenis_bubble_style', data.bubbleStyle);
    if (data.chatBubbleStyles) safeSetItem('aphenis_chat_bubble_styles', data.chatBubbleStyles);
    if (data.chatThemes) safeSetItem('aphenis_chat_themes', data.chatThemes);
    if (data.globalChatTheme) safeSetItem('aphenis_global_chat_theme', data.globalChatTheme);
    if (data.customThemes) safeSetItem('aphenis_custom_themes', data.customThemes);
    if (data.chatThemeExpiry) safeSetItem('aphenis_chat_theme_expiry', data.chatThemeExpiry);
    if (data.archivedChats) safeSetItem('aphenis_archived_chats', data.archivedChats);
    if (data.mutedChats) safeSetItem('aphenis_muted_chats', data.mutedChats);
    if (data.chatFolders) safeSetItem('aphenis_chat_folders', data.chatFolders);
    if (data.chatFolderAssignments) safeSetItem('aphenis_chat_folder_assignments', data.chatFolderAssignments);
    if (data.privacySettings) safeSetItem('vaultmsg_privacy_settings', data.privacySettings);
    if (data.contactSecurityLevels) safeSetItem('aphenis_contact_security_levels', data.contactSecurityLevels);
    if (data.readReceiptsOff) safeSetItem('aphenis_read_receipts_off', data.readReceiptsOff);
    if (data.vaultPin) safeSetItem('aphenis_vault_pin', data.vaultPin);
    if (data.chatPets) safeSetItem('aphenis_chat_pets', data.chatPets);
    if (data.petInteractions) safeSetItem('aphenis_pet_interactions', data.petInteractions);
    if (data.primaryPetChatId) safeSetItem('aphenis_primary_pet_chat_id', data.primaryPetChatId);
    if (data.petActivityLog) safeSetItem('aphenis_pet_activity_log', data.petActivityLog);
    if (data.petActivityFeed) safeSetItem('aphenis_pet_activity_feed', data.petActivityFeed);
    if (data.petLastInteractions) safeSetItem('aphenis_pet_last_interactions', data.petLastInteractions);
    if (data.petInteractionCounts) safeSetItem('aphenis_pet_interaction_counts', data.petInteractionCounts);
    if (data.petLifecycle) safeSetItem('aphenis_pet_lifecycle', data.petLifecycle);
    if (data.petRevives) safeSetItem('aphenis_pet_revives', data.petRevives);
    if (data.petFriendships) safeSetItem('aphenis_pet_friendships', data.petFriendships);
    if (data.petCoopCooldowns) safeSetItem('aphenis_pet_coop_cooldowns', data.petCoopCooldowns);
    if (data.petCoopHistory) safeSetItem('aphenis_pet_coop_history', data.petCoopHistory);
    if (data.petRoaming) safeSetItem('aphenis_pet_roaming', data.petRoaming);
    if (data.petPublic) safeSetItem('aphenis_pet_public', data.petPublic);
    if (data.petNativeReminders) safeSetItem('aphenis_pet_native_reminders', data.petNativeReminders);
    if (data.petCareLastReminded) safeSetItem('aphenis_pet_care_last_reminded', data.petCareLastReminded);
    if (data.petWorldHp) safeSetItem('aphenis_pet_world_hp', data.petWorldHp);
    if (data.perPetData && typeof data.perPetData === 'object') {
      for (const [key, value] of Object.entries(data.perPetData)) {
        if (typeof value === 'string') safeSetItem(key, value);
      }
    }
    if (data.petworldGraphics) safeSetItem('aphenis_petworld_graphics', data.petworldGraphics);
    if (data.petworldVolume) safeSetItem('aphenis_petworld_volume', data.petworldVolume);
    if (data.petworldMuted) safeSetItem('aphenis_petworld_muted', data.petworldMuted);
    if (data.petworldSfx) safeSetItem('aphenis_petworld_sfx', data.petworldSfx);
    if (data.petworldMusic) safeSetItem('aphenis_petworld_music', data.petworldMusic);
    if (data.petInventory) safeSetItem('aphenis_pet_inventory', data.petInventory);
    if (data.equippedSlots) safeSetItem('aphenis_equipped_slots', data.equippedSlots);
    if (data.petRecipesDiscovered) safeSetItem('aphenis_pet_recipes_discovered', data.petRecipesDiscovered);
    if (data.petWorldFogChunks) safeSetItem('aphenis_petworld_fog_chunks', data.petWorldFogChunks);
    if (data.petWorldMarkers) safeSetItem('aphenis_petworld_markers', data.petWorldMarkers);
    if (data.petWorldCombatStats) safeSetItem('aphenis_pet_world_combatStats', data.petWorldCombatStats);
    if (data.petWorldPurchased && typeof data.petWorldPurchased === 'object') {
      for (const [key, value] of Object.entries(data.petWorldPurchased)) {
        if (typeof value === 'string') safeSetItem(key, value);
      }
    }
    if (data.gameHistory) safeSetItem('aphenis_game_history', data.gameHistory);
    if (data.gameStatsPublic) safeSetItem('aphenis_game_stats_public', data.gameStatsPublic);
    if (data.lootPublic) safeSetItem('aphenis_loot_public', data.lootPublic);
    if (data.appXP) safeSetItem('aphenis_app_xp', data.appXP);
    if (data.xpLog) safeSetItem('aphenis_xp_log', data.xpLog);
    if (data.dailyLogin) safeSetItem('aphenis_daily_login', data.dailyLogin);
    if (data.levelRewardsClaimed) safeSetItem('aphenis_level_rewards_claimed', data.levelRewardsClaimed);
    if (data.levelRewardsEquipped) safeSetItem('aphenis_level_rewards_equipped', data.levelRewardsEquipped);
    if (data.chatStreaks) safeSetItem('aphenis_chat_streaks', data.chatStreaks);
    if (data.notificationCenter) safeSetItem('aphenis_notification_center', data.notificationCenter);
    if (data.achievements) safeSetItem('aphenis_achievements', data.achievements);
    if (data.achievementsSig) safeSetItem('aphenis_achievements_sig', data.achievementsSig);

    window.dispatchEvent(new Event('profile-updated'));
    return true;
  } catch (e) {
    console.error('[DataBackup] Decrypt/restore failed:', e);
    return false;
  }
}

// ─── Vault (Bookmarked Messages) ───
const VAULT_KEY = 'aphenis_vault_messages';

export interface VaultMessage {
  id: string;
  originalMessageId: string;
  chatId: string;
  chatName: string;
  senderId: string;
  content: string;
  originalTimestamp: number;
  savedAt: number;
}

export function getVaultMessages(): VaultMessage[] {
  try {
    const data = localStorage.getItem(VAULT_KEY);
    return data ? JSON.parse(data) : [];
  } catch { return []; }
}

export function addToVault(msg: Omit<VaultMessage, 'id' | 'savedAt'>): void {
  const vault = getVaultMessages();
  if (vault.some(v => v.originalMessageId === msg.originalMessageId)) return;
  vault.push({ ...msg, id: crypto.randomUUID(), savedAt: Date.now() });
  safeSetItem(VAULT_KEY, JSON.stringify(vault));
}

export function removeFromVault(id: string): void {
  const vault = getVaultMessages().filter(v => v.id !== id);
  safeSetItem(VAULT_KEY, JSON.stringify(vault));
}

export function isInVault(originalMessageId: string): boolean {
  return getVaultMessages().some(v => v.originalMessageId === originalMessageId);
}

// ─── Delete All Chat Data ───
export function deleteAllChatData(chatId: string): void {
  const messages = getMessages().filter(m => m.chatId !== chatId);
  _persistMessages(messages);
  deleteContact(chatId);
  deleteGroup(chatId);
  const scheduled = getScheduledMessages().filter(m => m.chatId !== chatId);
  safeSetItem(SCHEDULED_KEY, JSON.stringify(scheduled));
  clearChatTimer(chatId);
  const vault = getVaultMessages().filter(v => v.chatId !== chatId);
  safeSetItem(VAULT_KEY, JSON.stringify(vault));
  const readReceiptsOff = getReadReceiptsDisabledChats().filter(id => id !== chatId);
  safeSetItem(READ_RECEIPTS_KEY, JSON.stringify(readReceiptsOff));

  const PINNED_KEY = 'aphenis_pinned_chats';
  const ARCHIVED_KEY = 'aphenis_archived_chats';
  const MUTED_KEY = 'aphenis_muted_chats';
  try {
    const pinned = JSON.parse(localStorage.getItem(PINNED_KEY) || '[]').filter((id: string) => id !== chatId);
    safeSetItem(PINNED_KEY, JSON.stringify(pinned));
    const archived = JSON.parse(localStorage.getItem(ARCHIVED_KEY) || '[]').filter((id: string) => id !== chatId);
    safeSetItem(ARCHIVED_KEY, JSON.stringify(archived));
    const muted = JSON.parse(localStorage.getItem(MUTED_KEY) || '[]').filter((id: string) => id !== chatId);
    safeSetItem(MUTED_KEY, JSON.stringify(muted));
  } catch {}
  try {
    const themeData = localStorage.getItem('aphenis_chat_themes');
    if (themeData) {
      const themes = JSON.parse(themeData);
      delete themes[chatId];
      safeSetItem('aphenis_chat_themes', JSON.stringify(themes));
    }
  } catch {}
  try {
    const foldersData = localStorage.getItem('aphenis_chat_folder_assignments');
    if (foldersData) {
      const assignments = JSON.parse(foldersData);
      delete assignments[chatId];
      safeSetItem('aphenis_chat_folder_assignments', JSON.stringify(assignments));
    }
  } catch {}
  const petKeyPrefixes = ['aphenis_pet_lifecycle_', 'aphenis_pet_last_interactions_', 'aphenis_pet_interaction_counts_'];
  for (const prefix of petKeyPrefixes) localStorage.removeItem(`${prefix}${chatId}`);
  try {
    const primaryPetChatId = localStorage.getItem('aphenis_primary_pet_chat_id');
    if (primaryPetChatId === chatId) {
      localStorage.removeItem('aphenis_primary_pet_chat_id');
      window.dispatchEvent(new Event('pet-adopted'));
    }
  } catch {}
}

// ─── Deletion Requests ───
const DELETION_REQUESTS_KEY = 'aphenis_deletion_requests';

export interface DeletionRequest {
  id: string;
  fromUid: string;
  fromName: string;
  receivedAt: number;
}

export function getDeletionRequests(): DeletionRequest[] {
  try {
    const data = localStorage.getItem(DELETION_REQUESTS_KEY);
    return data ? JSON.parse(data) : [];
  } catch { return []; }
}

export function addDeletionRequest(req: Omit<DeletionRequest, 'id' | 'receivedAt'>): void {
  const requests = getDeletionRequests();
  if (requests.some(r => r.fromUid === req.fromUid)) return;
  requests.push({ ...req, id: crypto.randomUUID(), receivedAt: Date.now() });
  safeSetItem(DELETION_REQUESTS_KEY, JSON.stringify(requests));
}

export function removeDeletionRequest(id: string): void {
  const requests = getDeletionRequests().filter(r => r.id !== id);
  safeSetItem(DELETION_REQUESTS_KEY, JSON.stringify(requests));
}
